import React, { useState, useEffect, useCallback } from 'react';
import { AccountLinking } from './AccountLinking';

export function RadarDashboard({ shop }) {
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [accountLinked, setAccountLinked] = useState(false);
  const [user, setUser] = useState(null);
  const [competitors, setCompetitors] = useState([]);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);

  const connectShop = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/shopify/connect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop })
      });

      const data = await response.json();

      if (data.success) {
        setConnected(true);
        loadDashboardData();
      } else {
        setError(data.message || 'Connection failed');
      }
    } catch (err) {
      setError('Failed to connect shop');
      console.error('Shop connection error:', err);
    } finally {
      setLoading(false);
    }
  }, [shop, loadDashboardData]);

  const loadDashboardData = useCallback(async () => {
    try {
      const [competitorsRes, statsRes] = await Promise.all([
        fetch('/api/competitors'),
        fetch('/api/dashboard/stats')
      ]);

      const competitorsData = await competitorsRes.json();
      const statsData = await statsRes.json();

      if (competitorsData.success) {
        setCompetitors(competitorsData.data || []);
      }
      if (statsData.success) {
        setStats(statsData.data);
      }
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
    }
  }, []);

  const handleAccountLinked = useCallback((userData, session) => {
    setUser(userData);
    setAccountLinked(true);
    setConnected(true);
    loadDashboardData();
  }, [loadDashboardData]);

  useEffect(() => {
    // Check if shop is already connected and account is linked
    const checkConnection = async () => {
      try {
        const response = await fetch(`/api/shopify/status?shop=${shop}`);
        const data = await response.json();

        if (data.success && data.connected && data.accountLinked) {
          setConnected(true);
          setAccountLinked(true);
          setUser(data.user);
          loadDashboardData();
        }
      } catch (err) {
        console.error('Connection check failed:', err);
      } finally {
        setLoading(false);
      }
    };

    checkConnection();
  }, [shop, loadDashboardData]);

  // Show account linking if not connected or account not linked
  if (!connected || !accountLinked) {
    return <AccountLinking shop={shop} onAccountLinked={handleAccountLinked} />;
  }

  if (loading) {
    return (
      <s-page title="RADAR Intelligence">
        <s-section>
          <s-card>
            <s-stack align="center" justify="center">
              <s-spinner size="large"></s-spinner>
              <s-text>Loading your dashboard...</s-text>
            </s-stack>
          </s-card>
        </s-section>
      </s-page>
    );
  }

  if (error) {
    return (
      <s-page title="RADAR Intelligence">
        <s-section>
          <s-banner status="critical" title="Connection Error">
            {error}
          </s-banner>
        </s-section>
      </s-page>
    );
  }

  return (
    <s-page title="RADAR Intelligence">
      <s-section>
        <s-banner status="success" title="Connected">
          Your store {shop} is connected to RADAR Intelligence Platform
        </s-banner>
      </s-section>

      <s-section>
        <s-stack direction="inline" gap="base">
          <s-card>
            <s-stack direction="block" gap="base">
              <s-heading>Dashboard Overview</s-heading>
              <s-text tone="subdued">
                Welcome to RADAR! Your competitive intelligence dashboard is ready.
              </s-text>

              {stats && (
                <s-stack direction="block" gap="small">
                  <s-text>Competitors Tracked: {stats.competitorsCount || 0}</s-text>
                  <s-text>Active Alerts: {stats.alertsCount || 0}</s-text>
                  <s-text>Last Update: {stats.lastUpdate || 'Never'}</s-text>
                </s-stack>
              )}

              <s-button
                variant="primary"
                onClick={() => window.open('https://radar.aquivolabs.com/dashboard', '_blank')}
              >
                Open Full Dashboard
              </s-button>
            </s-stack>
          </s-card>

          <s-card>
            <s-stack direction="block" gap="base">
              <s-heading>Quick Actions</s-heading>

              <s-button onClick={connectShop}>
                Refresh Connection
              </s-button>

              <s-button
                onClick={() => window.open('https://radar.aquivolabs.com/competitors', '_blank')}
              >
                View Competitors
              </s-button>

              <s-button
                onClick={() => window.open('https://radar.aquivolabs.com/ads', '_blank')}
              >
                Browse Ad Library
              </s-button>
            </s-stack>
          </s-card>
        </s-stack>
      </s-section>

      {competitors.length > 0 && (
        <s-section>
          <s-card>
            <s-stack direction="block" gap="base">
              <s-heading>Recent Competitors</s-heading>
              {competitors.slice(0, 3).map((competitor, index) => (
                <s-stack key={index} direction="inline" justify="space-between" align="center">
                  <s-stack direction="block" gap="small">
                    <s-text variant="headingSm">{competitor.name}</s-text>
                    <s-text tone="subdued">{competitor.description}</s-text>
                  </s-stack>
                  <s-badge
                    status={competitor.status === 'active' ? 'success' : 'info'}
                  >
                    {competitor.status}
                  </s-badge>
                </s-stack>
              ))}
            </s-stack>
          </s-card>
        </s-section>
      )}

      <s-section>
        <s-card>
          <s-stack direction="block" gap="base">
            <s-heading>Need Help?</s-heading>
            <s-text>
              Visit our full dashboard for complete access to all features including
              competitor tracking, ad intelligence, and market insights.
            </s-text>
            <s-button
              variant="primary"
              onClick={() => window.open('https://radar.aquivolabs.com', '_blank')}
            >
              Launch RADAR Platform
            </s-button>
          </s-stack>
        </s-card>
      </s-section>
    </s-page>
  );
}